import { goRequest, goClient } from '../utils/httpClientTs';
import {
  FinanceFundsListParams,
  FinanceFundsListResponse,
  FundDetailResponse,
  PatchFundRequest,
  CreateFundNoteRequest,
  PatchFundNoteRequest,
  CreateFundPositionRequest,
  ReplaceFundPositionRequest,
  PatchFundPositionRequest,
  CreateFundRoleSlotRequest,
  PatchFundRoleSlotRequest,
  CreateFundEmailContactRequest,
  PatchFundEmailContactRequest,
  FundChairRoleHistoryResponse,
  FundChairOtherFundsResponse,
  CreateFundRequest,
  CreateFundResponse,
  CreateFundNamedAmountRequest,
  PatchFundNamedAmountRequest,
  FundNamedAmountDTO,
  FundAttachmentType,
  FundAttachmentsGroupedResponse,
  FundAttachmentItem,
  PatchFundAttachmentRequest,
  FundHistoryResponse,
  FundStatementDatesResponse,
  FundStatementDataResponse,
  PostFundStatementOverrideRequest,
} from '../types/financeFunds';

export const getFinanceFundsList = async (
  params: FinanceFundsListParams
): Promise<FinanceFundsListResponse> => {
  let url = `/finance/funds?page_size=${params.page_size}&page=${params.page}`;

  if (params.search) {
    url += `&search=${encodeURIComponent(params.search)}`;
  }
  if (params.order_by) {
    url += `&order_by=${encodeURIComponent(params.order_by)}`;
  }
  if (params.order) {
    url += `&order=${encodeURIComponent(params.order)}`;
  }
  if (params.local_chapter_id) {
    url += `&local_chapter_id=${encodeURIComponent(params.local_chapter_id)}`;
  }
  if (params.spd_chapter_id) {
    url += `&spd_chapter_id=${encodeURIComponent(params.spd_chapter_id)}`;
  }
  if (params.status) {
    url += `&status=${encodeURIComponent(params.status)}`;
  }

  return goRequest<FinanceFundsListResponse>({
    url: url,
    method: 'GET',
  });
};

export const createFund = async (
  data: CreateFundRequest
): Promise<CreateFundResponse> => {
  return goRequest<CreateFundResponse>({
    url: '/finance/funds',
    method: 'POST',
    data,
  });
};

export const getFundDetail = async (
  fundId: string
): Promise<FundDetailResponse> => {
  return goRequest<FundDetailResponse>({
    url: `/finance/funds/${fundId}`,
    method: 'GET',
  });
};

export const getFundHistory = async (
  fundId: string
): Promise<FundHistoryResponse> => {
  return goRequest<FundHistoryResponse>({
    url: `/finance/funds/${fundId}/history`,
    method: 'GET',
  });
};

export const getFundStatementDates = async (
  fundId: string
): Promise<FundStatementDatesResponse> => {
  return goRequest<FundStatementDatesResponse>({
    url: `/finance/funds/${fundId}/statement-data/dates`,
    method: 'GET',
  });
};

export const getFundStatementData = async (
  fundId: string,
  asOfDate: string
): Promise<FundStatementDataResponse> => {
  return goRequest<FundStatementDataResponse>({
    url: `/finance/funds/${fundId}/statement-data?as_of_date=${encodeURIComponent(asOfDate)}`,
    method: 'GET',
  });
};

export const postFundStatementOverride = async (
  fundId: string,
  data: PostFundStatementOverrideRequest
): Promise<FundStatementDataResponse> => {
  return goRequest<FundStatementDataResponse>({
    url: `/finance/funds/${fundId}/statement-data/overrides`,
    method: 'POST',
    data,
  });
};

export const downloadFundStatementPdf = async (
  fundId: string,
  asOfDate: string
): Promise<void> => {
  const res = await goClient.get(
    `/finance/funds/${fundId}/statement-data/pdf?as_of_date=${encodeURIComponent(asOfDate)}`,
    { responseType: 'blob' }
  );
  const blob = new Blob([res.data], { type: 'application/pdf' });
  const href = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = href;
  a.download = `fund-statement-${asOfDate}.pdf`;
  a.click();
  window.URL.revokeObjectURL(href);
};

export const saveFundStatementPrincipalRetained = async (
  fundId: string,
  asOfDate: string
): Promise<FundStatementDataResponse> => {
  return goRequest<FundStatementDataResponse>({
    url: `/finance/funds/${fundId}/statement-data/principal-retained`,
    method: 'POST',
    data: { as_of_date: asOfDate },
  });
};

export const patchFund = async (
  fundId: string,
  data: PatchFundRequest
): Promise<void> => {
  await goRequest({
    url: `/finance/funds/${fundId}`,
    method: 'PATCH',
    data,
  });
};

export const createFundNote = async (
  fundId: string,
  data: CreateFundNoteRequest
): Promise<void> => {
  await goRequest({
    url: `/finance/funds/${fundId}/notes`,
    method: 'POST',
    data,
  });
};

export const patchFundNote = async (
  fundId: string,
  noteId: string,
  data: PatchFundNoteRequest
): Promise<void> => {
  await goRequest({
    url: `/finance/funds/${fundId}/notes/${noteId}`,
    method: 'PATCH',
    data,
  });
};

export const deleteFundNote = async (
  fundId: string,
  noteId: string
): Promise<void> => {
  await goRequest({
    url: `/finance/funds/${fundId}/notes/${noteId}`,
    method: 'DELETE',
  });
};

export const createFundNamedAmount = async (
  fundId: string,
  data: CreateFundNamedAmountRequest
): Promise<FundNamedAmountDTO> => {
  return goRequest<FundNamedAmountDTO>({
    url: `/finance/funds/${fundId}/named-amounts`,
    method: 'POST',
    data,
  });
};

export const patchFundNamedAmount = async (
  fundId: string,
  amountId: string,
  data: PatchFundNamedAmountRequest
): Promise<void> => {
  await goRequest({
    url: `/finance/funds/${fundId}/named-amounts/${amountId}`,
    method: 'PATCH',
    data,
  });
};

export const deleteFundNamedAmount = async (
  fundId: string,
  amountId: string
): Promise<void> => {
  await goRequest({
    url: `/finance/funds/${fundId}/named-amounts/${amountId}`,
    method: 'DELETE',
  });
};

export const createFundPosition = async (
  fundId: string,
  data: CreateFundPositionRequest
): Promise<void> => {
  await goRequest({
    url: `/finance/funds/${fundId}/positions`,
    method: 'POST',
    data,
  });
};

export const patchFundPosition = async (
  fundId: string,
  memberId: string,
  data: PatchFundPositionRequest
): Promise<void> => {
  await goRequest({
    url: `/finance/funds/${fundId}/positions/${memberId}`,
    method: 'PATCH',
    data,
  });
};

export const deleteFundPosition = async (
  fundId: string,
  memberId: string
): Promise<void> => {
  await goRequest({
    url: `/finance/funds/${fundId}/positions/${memberId}`,
    method: 'DELETE',
  });
};

export const replaceFundPosition = async (
  fundId: string,
  memberId: string,
  data: ReplaceFundPositionRequest
): Promise<void> => {
  await goRequest({
    url: `/finance/funds/${fundId}/positions/replace?member_id=${encodeURIComponent(memberId)}`,
    method: 'POST',
    data,
  });
};

export const getFundChairRoleHistory = async (
  fundId: string,
  roleId: string
): Promise<FundChairRoleHistoryResponse> => {
  return goRequest<FundChairRoleHistoryResponse>({
    url: `/finance/funds/${fundId}/chair-role-history?role_id=${encodeURIComponent(roleId)}`,
    method: 'GET',
  });
};

export const getFundChairOtherFunds = async (
  fundId: string,
  userId: string,
  roleId: string
): Promise<FundChairOtherFundsResponse> => {
  return goRequest<FundChairOtherFundsResponse>({
    url: `/finance/funds/${fundId}/chair-other-funds?user_id=${encodeURIComponent(userId)}&role_id=${encodeURIComponent(roleId)}`,
    method: 'GET',
  });
};

export const createFundChairRoleSlot = async (
  fundId: string,
  data: CreateFundRoleSlotRequest
): Promise<void> => {
  await goRequest({
    url: `/finance/funds/${fundId}/chair-role-slots`,
    method: 'POST',
    data,
  });
};

export const patchFundChairRoleSlot = async (
  fundId: string,
  slotId: string,
  data: PatchFundRoleSlotRequest
): Promise<void> => {
  await goRequest({
    url: `/finance/funds/${fundId}/chair-role-slots/${slotId}`,
    method: 'PATCH',
    data,
  });
};

export const deleteFundChairRoleSlot = async (
  fundId: string,
  slotId: string
): Promise<void> => {
  await goRequest({
    url: `/finance/funds/${fundId}/chair-role-slots/${slotId}`,
    method: 'DELETE',
  });
};

export const createFundEmailContact = async (
  fundId: string,
  data: CreateFundEmailContactRequest
): Promise<void> => {
  await goRequest({
    url: `/finance/funds/${fundId}/email-contacts`,
    method: 'POST',
    data,
  });
};

export const patchFundEmailContact = async (
  fundId: string,
  memberId: string,
  data: PatchFundEmailContactRequest
): Promise<void> => {
  await goRequest({
    url: `/finance/funds/${fundId}/email-contacts/${memberId}`,
    method: 'PATCH',
    data,
  });
};

export const deleteFundEmailContact = async (
  fundId: string,
  memberId: string
): Promise<void> => {
  await goRequest({
    url: `/finance/funds/${fundId}/email-contacts/${memberId}`,
    method: 'DELETE',
  });
};

export const createFundEmailRoleSlot = async (
  fundId: string,
  data: CreateFundRoleSlotRequest
): Promise<void> => {
  await goRequest({
    url: `/finance/funds/${fundId}/email-role-slots`,
    method: 'POST',
    data,
  });
};

export const patchFundEmailRoleSlot = async (
  fundId: string,
  slotId: string,
  data: PatchFundRoleSlotRequest
): Promise<void> => {
  await goRequest({
    url: `/finance/funds/${fundId}/email-role-slots/${slotId}`,
    method: 'PATCH',
    data,
  });
};

export const deleteFundEmailRoleSlot = async (
  fundId: string,
  slotId: string
): Promise<void> => {
  await goRequest({
    url: `/finance/funds/${fundId}/email-role-slots/${slotId}`,
    method: 'DELETE',
  });
};

export const listFundAttachmentTypes = async (): Promise<
  FundAttachmentType[]
> => {
  return goRequest<FundAttachmentType[]>({
    url: '/finance/fund-attachment-types',
    method: 'GET',
  });
};

export const listFundAttachments = async (
  fundId: string
): Promise<FundAttachmentsGroupedResponse> => {
  return goRequest<FundAttachmentsGroupedResponse>({
    url: `/finance/funds/${fundId}/attachments`,
    method: 'GET',
  });
};

export const uploadFundAttachment = async (
  fundId: string,
  file: File,
  attachmentTypeId: string,
  description?: string
): Promise<FundAttachmentItem> => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('attachment_type_id', attachmentTypeId);
  if (description?.trim()) {
    formData.append('description', description.trim());
  }
  return goRequest<FundAttachmentItem>({
    url: `/finance/funds/${fundId}/attachments`,
    method: 'POST',
    data: formData,
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
};

export const updateFundAttachment = async (
  fundId: string,
  attachmentId: string,
  data: PatchFundAttachmentRequest
): Promise<void> => {
  await goRequest({
    url: `/finance/funds/${fundId}/attachments/${attachmentId}`,
    method: 'PATCH',
    data,
  });
};

export const deleteFundAttachment = async (
  fundId: string,
  attachmentId: string
): Promise<void> => {
  await goRequest({
    url: `/finance/funds/${fundId}/attachments/${attachmentId}`,
    method: 'DELETE',
  });
};

export const downloadFundAttachment = async (
  fundId: string,
  attachmentId: string
): Promise<{ blob: Blob; filename?: string }> => {
  const res = await goClient.get(
    `/finance/funds/${fundId}/attachments/${attachmentId}/download`,
    { responseType: 'blob' }
  );
  const blob = res.data as Blob;
  const contentDisposition = res.headers?.['content-disposition'];
  let filename: string | undefined;
  if (contentDisposition) {
    const match = contentDisposition.match(
      /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/
    );
    if (match?.[1]) {
      filename = match[1].replace(/['"]/g, '').trim();
    }
  }
  return { blob, filename };
};
